import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { CircleAlert, Plus, ShieldCheck } from 'lucide-react';
import { Banner, Button, Drawer, Field, Input, PlatformIcon, Select } from '@/components/ui';
import { FilesSiteForm } from '@/components/sites/FilesSiteForm';
import {
  BackupPolicySelect,
  NodeSelect,
  TemplateVersionSelect,
  type SitePolicy,
} from '@/components/sites/SiteFormFields';
import { idempotencyKey } from '@/utils/idempotency';
import { useNodes } from '@/hooks/useNodes';
import { useCreateSite } from '@/hooks/useSites';
import { useActiveTemplates } from '@/hooks/useTemplates';
import { toast } from '@/store/toastStore';
import { errorCode, errorField, errorText } from '@/utils/errors';
import { isSchedulable } from '@/utils/nodes';
import { suggestSlug } from '@/utils/slug';
import { isDomainName, isEmail, isSlug, splitList } from '@/utils/validation';

type Kind = 'wordpress' | 'files';

/** `platform` names the mark the choice is drawn with. */
const KINDS: readonly { id: Kind; platform: string; title: string; text: string }[] = [
  { id: 'wordpress', platform: 'wordpress', title: 'WordPress', text: 'Из шаблона, на сервере панели.' },
  { id: 'files', platform: 'static', title: 'Сайт из файлов', text: 'Статический сайт на внешнем сервере с nginx или Apache.' },
];

/**
 * A new site: WordPress from a template, in one form — or a site of files on
 * an external server (ADR-0044), whatever makes its pages.
 *
 * A drawer rather than a page (ADR-0016): the operator starts from the list
 * and comes back to it, and nothing here needs more than one screen. Submitting
 * queues the creation and opens the new site's page at once, where progress
 * shows up; the form does not wait for minutes of work it cannot speed up.
 */

type Tier = 'production' | 'staging';

export function CreateSiteDrawer({
  open,
  onClose,
  domain = null,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  /** A domain just bought (ADR-0053), put in as the site's address. */
  readonly domain?: string | null;
}): ReactNode {
  const navigate = useNavigate();

  const [kind, setKind] = useState<Kind>('wordpress');
  const [filesBusy, setFilesBusy] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [primaryDomain, setPrimaryDomain] = useState(domain ?? '');
  useEffect(() => {
    if (open && domain) setPrimaryDomain(domain);
  }, [open, domain]);
  const [aliases, setAliases] = useState('');
  const [templateVersionId, setTemplateVersionId] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [tier, setTier] = useState<Tier>('staging');
  const [backupPolicy, setBackupPolicy] = useState<SitePolicy>('standard');
  const [adminLogin, setAdminLogin] = useState('siteadmin');
  const [adminEmail, setAdminEmail] = useState('');
  const [shown, setShown] = useState(false);

  // One key per filled-in form: pressing "Создать" twice, or retrying after a
  // dropped connection, is the same request, not a second site.
  const key = useRef(idempotencyKey('site.create'));

  const templates = useActiveTemplates({ enabled: open });
  const nodes = useNodes({ enabled: open });

  const versions = templates.data ?? [];
  const chosenVersion = templateVersionId || versions[0]?.id || '';
  const schedulable = (nodes.data ?? []).filter(isSchedulable);

  const effectiveSlug = slugTouched ? slug : suggestSlug(displayName);
  const aliasList = splitList(aliases);

  // Checked here so the answer lands beside the field; the server checks the
  // same rules and has the last word.
  const problems: Partial<Record<'displayName' | 'slug' | 'primaryDomain' | 'aliases' | 'adminEmail', string>> = {};
  if (!displayName.trim()) problems.displayName = 'Нужно название';
  if (!effectiveSlug) problems.slug = 'Нужен идентификатор';
  else if (!isSlug(effectiveSlug)) problems.slug = 'Только строчная латиница, цифры и дефис; не с дефиса';
  if (!primaryDomain.trim()) problems.primaryDomain = 'Нужен домен';
  else if (!isDomainName(primaryDomain.trim().toLowerCase())) problems.primaryDomain = 'Не похоже на доменное имя';
  if (!isEmail(adminEmail.trim())) problems.adminEmail = 'Нужна почта администратора';
  const badAlias = aliasList.find((item) => !isDomainName(item));
  if (badAlias) problems.aliases = `Не похоже на доменное имя: ${badAlias}`;
  else if (new Set([primaryDomain.trim().toLowerCase(), ...aliasList]).size !== aliasList.length + 1 && aliasList.length > 0) {
    problems.aliases = 'Домены повторяются';
  }

  const create = useCreateSite({
    onSuccess: (result) => {
      toast.success('Создание сайта поставлено в очередь');
      key.current = idempotencyKey('site.create');
      onClose();
      navigate(`/sites/${result.siteId}`);
    },
  });

  // The server's verdict wins over the local one once there is one, and is
  // placed beside the field it is about.
  const blamed = create.isError ? errorField(create.error) : null;
  const serverSlug = create.isError && (blamed === 'slug' || errorCode(create.error) === 'SITE_SLUG_TAKEN');
  const serverDomain = create.isError && errorCode(create.error) === 'DOMAIN_ALREADY_BOUND';

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setShown(true);
    if (Object.keys(problems).length > 0 || !chosenVersion || create.isPending) return;
    create.mutate({
      input: {
        slug: effectiveSlug,
        displayName: displayName.trim(),
        primaryDomain: primaryDomain.trim().toLowerCase(),
        aliases: aliasList,
        templateVersionId: chosenVersion,
        ...(nodeId ? { nodeId } : {}),
        tier,
        backupPolicy,
        adminLogin: adminLogin.trim(),
        adminEmail: adminEmail.trim(),
      },
      key: key.current,
    });
  };

  const choice = (
    <div className="kind-choice" role="radiogroup" aria-label="Какой сайт">
      {KINDS.map(({ id, platform, title, text }) => (
        <label key={id} className={`choice${kind === id ? ' choice-active' : ''}`}>
          <input type="radio" name="site-kind" value={id} checked={kind === id} onChange={() => setKind(id)} disabled={create.isPending || filesBusy} />
          <span className="stack stack-tight">
            <span className="row-tight strong">
              <PlatformIcon platform={platform} />
              {title}
            </span>
            <span className="text-sm muted">{text}</span>
          </span>
        </label>
      ))}
    </div>
  );

  return (
    <Drawer
      open={open}
      title="Новый сайт"
      description={kind === 'wordpress' ? 'WordPress из шаблона' : 'Сайт из файлов на внешнем сервере'}
      onClose={onClose}
      width="md"
      footer={
        <>
          <Button onClick={onClose} disabled={create.isPending || filesBusy}>
            Отмена
          </Button>
          <Button
            type="submit"
            form={kind === 'wordpress' ? 'create-site' : 'create-files-site'}
            variant="primary"
            icon={Plus}
            busy={kind === 'wordpress' ? create.isPending : filesBusy}
            disabled={kind === 'wordpress' && !chosenVersion}
          >
            Создать сайт
          </Button>
        </>
      }
    >
      {kind === 'files' ? <FilesSiteForm formId="create-files-site" header={choice} open={open} onBusy={setFilesBusy} onDone={onClose} /> : null}
      {/* Kept, only hidden, while a site of files is filled in: going back finds it as it was left. */}
      <form id="create-site" className="stack" onSubmit={submit} noValidate hidden={kind !== 'wordpress'}>
        {choice}
        <Field
          label="Название"
          htmlFor="site-name"
          hint="Отображается в панели и в заголовке сайта"
          {...(shown && problems.displayName ? { error: problems.displayName } : {})}
        >
          <Input
            id="site-name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            maxLength={160}
            autoComplete="off"
            required
            autoFocus
          />
        </Field>

        <Field
          label="Идентификатор"
          htmlFor="site-slug"
          hint="Имя базы данных и контейнера; не изменяется"
          {...((shown || (slugTouched && slug !== '')) && problems.slug ? { error: problems.slug } : {})}
          {...(serverSlug ? { error: errorText(create.error) } : {})}
        >
          <Input
            id="site-slug"
            className="mono"
            value={effectiveSlug}
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value.toLowerCase());
            }}
            maxLength={63}
            autoComplete="off"
            spellCheck={false}
            required
          />
        </Field>

        <Field
          label="Основной домен"
          htmlFor="site-domain"
          {...(shown && problems.primaryDomain ? { error: problems.primaryDomain } : {})}
          {...(serverDomain ? { error: errorText(create.error) } : {})}
        >
          <Input
            id="site-domain"
            className="mono"
            value={primaryDomain}
            onChange={(event) => setPrimaryDomain(event.target.value)}
            placeholder="example.com"
            autoComplete="off"
            spellCheck={false}
            required
          />
        </Field>

        <Field
          label="Дополнительные домены"
          htmlFor="site-aliases"
          hint="Через пробел или запятую. Необязательно"
          {...(shown && problems.aliases ? { error: problems.aliases } : {})}
        >
          <Input
            id="site-aliases"
            className="mono"
            value={aliases}
            onChange={(event) => setAliases(event.target.value)}
            placeholder="www.example.com"
            autoComplete="off"
            spellCheck={false}
          />
        </Field>

        <div className="form-grid">
          <Field label="Шаблон" htmlFor="site-template">
            <TemplateVersionSelect
              id="site-template"
              value={chosenVersion}
              onChange={setTemplateVersionId}
              versions={versions}
              loading={templates.isPending}
            />
          </Field>

          <Field label="Сервер" htmlFor="site-node">
            <NodeSelect id="site-node" value={nodeId} onChange={setNodeId} nodes={schedulable} />
          </Field>

          <Field label="Среда" htmlFor="site-tier">
            <Select
              id="site-tier"
              label="Среда"
              value={tier}
              onChange={setTier}
              options={[
                { value: 'staging', label: 'Тестовый', hint: 'Закрыт от индексации' },
                { value: 'production', label: 'Рабочий', hint: 'Открыт для индексации' },
              ]}
            />
          </Field>

          <Field label="Политика копий" htmlFor="site-policy">
            <BackupPolicySelect id="site-policy" value={backupPolicy} onChange={setBackupPolicy} />
          </Field>
        </div>

        <fieldset className="field fieldset">
          <legend className="field-label">Администратор WordPress</legend>
          <div className="form-grid">
            <Field label="Логин" htmlFor="site-admin-login" hint="Не используйте admin">
              <Input
                id="site-admin-login"
                value={adminLogin}
                onChange={(event) => setAdminLogin(event.target.value)}
                minLength={3}
                maxLength={60}
                autoComplete="off"
                spellCheck={false}
                required
              />
            </Field>
            <Field
              label="Почта"
              htmlFor="site-admin-email"
              {...(shown && problems.adminEmail ? { error: problems.adminEmail } : {})}
            >
              <Input
                id="site-admin-email"
                type="email"
                value={adminEmail}
                onChange={(event) => setAdminEmail(event.target.value)}
                autoComplete="off"
                required
              />
            </Field>
          </div>
        </fieldset>

        <Banner tone="info" icon={ShieldCheck}>
          Пароль генерируется автоматически и показывается один раз на странице сайта.
        </Banner>

        {create.isError && !serverSlug && !serverDomain ? (
          <p className="field-error" role="alert">
            <CircleAlert size={14} aria-hidden="true" />
            {errorText(create.error, 'Не удалось поставить создание в очередь')}
          </p>
        ) : null}
      </form>
    </Drawer>
  );
}
